import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import dayjs from "dayjs";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import { catalogService } from "../../utils/services/catalogService";
import { catalogProductService } from "../../utils/services/catalogProductService";
import { catalogTypeService } from "../../utils/services/catalogTypeService";
import { productService } from "../../utils/services/productService";
import { clientService } from "../../utils/services/clientService";
import { useDarkMode } from "../../components/context/darkModeContext";
import { fieldSx } from "./constants";
import FieldLabel from "./components/FieldLabel";
import SectionPaper from "./components/SectionPaper";
import ProductsDesktopTable from "./components/ProductsDesktopTable";
import ProductEntry from "./components/ProductEntry";
import ImportProductsModal from "./components/ImportProductsModal";
import PageBoardModal from "./components/PageBoardModal";

export default function ManageCatalog() {
  const navigate = useNavigate();
  const clientId = localStorage.getItem("client_id");
  const { colors } = useDarkMode();
  const isDesktop = useMediaQuery("(min-width:900px)");

  const initialForm = {
    uid: "",
    name: "",
    type: null,
    client_id: null,
    valid_from: "",
    valid_till: "",
    description: "",
  };

  const emptyRow = { product: null, page: "", variants: [], productSkus: [] };

  const [catalogId, setCatalogId] = useState("");
  const [formData, setFormData] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});
  const [products, setProducts] = useState([emptyRow]); // Array of { product, page, variants: [{sku, price, _suggestedPrice}] }
  const [loading, setLoading] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [pageBoardOpen, setPageBoardOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(null); // 'valid_from' | 'valid_till' | null
  const [calendarPosition, setCalendarPosition] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const calendarRef = useRef(null);
  const validFromRef = useRef(null);
  const validTillRef = useRef(null);
  const client_id = localStorage.getItem("client_id");

  // ── Fetch existing catalog by id ──
  useEffect(() => {
    const id = window.location.href.split("/").pop();
    setCatalogId(id);
    if (id && !["create", "bulk"].includes(id)) {
      fetchExisting(id);
    }
    if (id === "bulk") {
      const existing = localStorage.getItem("catalogImportResult");
      if (existing) {
        setLoading(true);
        const parsed = JSON.parse(existing);
        productService
          .fetchProductsPost({ _id: { $in: parsed.map((p) => p.product_id) } })
          .then((res) => {
            const products = res || [];
            setProducts(
              products
                .map((p) => {
                  const variants = (p.variants || [])
                    .filter((v) => !v.parent)
                    .map((v) => ({
                      sku: v.sku || "",
                      price: v.price != null ? v.price : "",
                      _suggestedSku: v.sku || "",
                      _suggestedPrice: v.price != null ? v.price : null,
                    }));
                  return {
                    product: p,
                    page:
                      parsed.find((x) => x.product_id === p._id)?.page || "",
                    variants: variants,
                    productSkus: variants.map((v) => ({
                      sku: v.sku,
                      price: v.price,
                    })),
                  };
                })
                .sort((a, b) => a.page - b.page),
            );
            setLoading(false);
            // Remove the import result from localStorage after processing
            localStorage.removeItem("catalogImportResult");
          })
          .catch(() => {
            setProducts([]);
            setLoading(false);
            toast.error("Failed to fetch products for imported catalog.");
            // Remove the import result from localStorage even on error
            localStorage.removeItem("catalogImportResult");
          });
      }
    }
  }, []);

  const fetchExisting = async (id) => {
    setLoading(true);
    try {
      const [data, cpResult] = await Promise.all([
        catalogService.fetchById(id),
        catalogProductService.fetchByCatalog(id),
      ]);
      setFormData({
        uid: data.uid || "",
        name: data.name || "",
        type: data.type || null,
        client_id: data.client_id || null,
        valid_from: data.valid_from
          ? new Date(data.valid_from).toISOString().split("T")[0]
          : "",
        valid_till: data.valid_till
          ? new Date(data.valid_till).toISOString().split("T")[0]
          : "",
        description: data.description || "",
      });

      // Build products state from catalogProduct collection
      const cpItems = cpResult?.data || cpResult || [];
      const prods = cpItems
        .map((cp) => {
          const variantList = (cp.variants || []).map((v) => ({
            sku: v.sku || "",
            price: v.price ?? "",
            _suggestedSku: "",
            _suggestedPrice: null,
          }));
          return {
            product: cp.product || null,
            page: cp.page ?? "",
            variants: variantList,
            productSkus: variantList.map((v) => ({
              sku: v.sku,
              price: v.price,
            })),
          };
        })
        .sort((a, b) => (a.page || 0) - (b.page || 0));
      setProducts(
        prods.length > 0
          ? prods
          : [{ product: null, page: "", variants: [], productSkus: [] }],
      );
    } catch (err) {
      console.error("Failed to fetch catalog:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Handle product selection → populate variants ──
  const handleProductChange = useCallback(
    async (entryIndex, selectedProduct) => {
      if (!selectedProduct) {
        setProducts((prev) =>
          prev.map((p, i) =>
            i === entryIndex
              ? { ...p, product: null, variants: [], page: "" }
              : p,
          ),
        );
        return;
      }

      // Fetch full product details for variants
      let productVariants = [];
      try {
        const full = await productService.fetchById(selectedProduct._id);
        productVariants = (full.variants || [])
          .filter((v) => !v.parent) // only parent variants
          .map((v) => ({
            sku: v.sku || "",
            price: v.price != null ? v.price : "",
            _suggestedSku: v.sku || "",
            _suggestedPrice: v.price != null ? v.price : null,
          }));
      } catch {
        productVariants = [];
      }

      setProducts((prev) => {
        const updated = prev.map((p, i) =>
          i === entryIndex
            ? {
                ...p,
                product: selectedProduct,
                variants: productVariants,
                productSkus: productVariants.map((v) => ({
                  sku: v.sku,
                  price: v.price,
                })),
                page: p.page || "",
              }
            : p,
        );
        // Auto-add empty row if this was the last row
        const isLastRow = entryIndex === prev.length - 1;
        if (isLastRow) {
          return [
            ...updated,
            { product: null, page: "", variants: [], productSkus: [] },
          ];
        }
        return updated;
      });
    },
    [],
  );

  const handleVariantChange = useCallback(
    (entryIndex, variantIndex, field, value) => {
      setProducts((prev) =>
        prev.map((p, i) =>
          i === entryIndex
            ? {
                ...p,
                variants: p.variants.map((v, vi) =>
                  vi === variantIndex ? { ...v, [field]: value } : v,
                ),
              }
            : p,
        ),
      );
    },
    [],
  );

  const handlePageChange = useCallback((entryIndex, value) => {
    setProducts((prev) =>
      prev.map((p, i) => (i === entryIndex ? { ...p, page: value } : p)),
    );
  }, []);

  const handleAddProduct = useCallback(() => {
    setProducts((prev) => [
      ...prev,
      { product: null, page: "", variants: [], productSkus: [] },
    ]);
  }, []);

  const handleRemoveProduct = useCallback((index) => {
    setProducts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleToggleProductSelect = useCallback((productId) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }, []);

  const handleSelectAllProducts = () => {
    const withProduct = products.filter((p) => p.product);
    if (
      withProduct.length > 0 &&
      withProduct.every((p) => selectedProducts.has(p.product._id))
    ) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(withProduct.map((p) => p.product._id)));
    }
  };

  const handleDeleteSelectedProducts = () => {
    const pageKey = (p) => {
      const pg = p.page;
      if (pg === "" || pg === null || pg === undefined) return Infinity;
      return Number(pg) || Infinity;
    };
    setProducts((prev) => {
      const filtered = prev.filter(
        (p) => !p.product || !selectedProducts.has(p.product._id),
      );
      if (!filtered.some((p) => !p.product)) {
        return [...filtered, emptyRow].sort((a, b) => pageKey(a) - pageKey(b));
      }
      return filtered.sort((a, b) => pageKey(a) - pageKey(b));
    });
    setSelectedProducts(new Set());
  };

  const handleVariantsChange = useCallback((entryIndex, newVariants) => {
    setProducts((prev) =>
      prev.map((p, i) =>
        i === entryIndex ? { ...p, variants: newVariants } : p,
      ),
    );
  }, []);

  const handleImportProducts = async (
    filterType,
    filterItem,
    mode = "concat",
    perPage = 0,
    maxPages = 0,
    statusFilter = null,
  ) => {
    setImportLoading(true);
    const sortByPage = (arr) => {
      const key = (p) => {
        const pg = p.page;
        if (pg === "" || pg === null || pg === undefined) return Infinity;
        return Number(pg) || Infinity;
      };
      return arr.slice().sort((a, b) => key(a) - key(b));
    };
    const makeComputePage = (totalCount) => {
      const ep =
        perPage > 0
          ? perPage
          : maxPages > 0
            ? Math.ceil(totalCount / maxPages)
            : 0;
      return (idx, startPage) =>
        ep > 0 ? startPage + Math.floor(idx / ep) : "";
    };
    try {
      const data = await productService.fetchProductsPost({
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(filterType === "category" && filterItem.length > 0
          ? { product_type: { $in: filterItem.map((item) => item._id) } }
          : {}),
        ...(filterType === "supplier" && filterItem.length > 0
          ? { vendor: { $in: filterItem.map((item) => item._id) } }
          : {}),
        ...(filterType === "attribute" &&
        filterItem?.attributeType &&
        filterItem?.attributeValues?.length > 0
          ? filterItem.attributeType === "Tags"
            ? {
                $or: [
                  {
                    "variants.option.option_type": {
                      $regex: `^${filterItem.attributeType.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`,
                      $options: "i",
                    },
                    "variants.option.option_value": {
                      $regex: filterItem.attributeValues
                        .map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
                        .join("|"),
                      $options: "i",
                    },
                  },
                  {
                    tags: {
                      $in: filterItem.attributeValues,
                    },
                  },
                ],
              }
            : {
                "variants.option.option_type": {
                  $regex: `^${filterItem.attributeType.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`,
                  $options: "i",
                },
                "variants.option.option_value": {
                  $regex: filterItem.attributeValues
                    .map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
                    .join("|"),
                  $options: "i",
                },
              }
          : {}),

        client_id: formData.client_id?._id || formData.client_id || client_id,
      });
      const fetched = data?.data || data || [];
      if (!fetched.length) {
        toast.info("No products found for the selected filter.");
        return;
      }

      const toRow = (p, assignedPage = "") => ({
        product: p,
        page: assignedPage,
        variants: (p.variants || [])
          .filter((v) => !v.parent)
          .map((v) => ({
            sku: v.sku || "",
            price: v.price != null ? v.price : "",
            _suggestedSku: v.sku || "",
            _suggestedPrice: v.price != null ? v.price : null,
          })),
        productSkus: (p.variants || [])
          .filter((v) => !v.parent)
          .map((v) => ({ sku: v.sku, price: v.price })),
      });

      if (mode === "replace") {
        const computePage = makeComputePage(fetched.length);
        const newRows = fetched.map((p, idx) => toRow(p, computePage(idx, 1)));
        setProducts(sortByPage([...newRows, emptyRow]));
        toast.success(`List replaced with ${newRows.length} product(s).`);
        return;
      }

      // concat mode — skip already-present products
      const existingIds = new Set(
        products.map((p) => p.product?._id).filter(Boolean),
      );
      const freshItems = fetched.filter((p) => !existingIds.has(p._id));
      if (!freshItems.length) {
        toast.info("All fetched products are already in the list.");
        return;
      }
      const computePage = makeComputePage(freshItems.length);
      const hasPageAssignment = perPage > 0 || maxPages > 0;
      const maxExistingPage = hasPageAssignment
        ? products
            .filter((p) => p.product && p.page !== "" && p.page != null)
            .reduce((max, p) => Math.max(max, Number(p.page) || 0), 0)
        : 0;
      const startPage = maxExistingPage + 1;
      const newRows = freshItems.map((p, idx) =>
        toRow(p, computePage(idx, startPage)),
      );
      setProducts((prev) => {
        const base = prev.filter((p) => p.product);
        return sortByPage([...base, ...newRows, emptyRow]);
      });
      toast.success(`${newRows.length} product(s) imported.`);
    } catch (err) {
      toast.error("Failed to import products.");
    } finally {
      setImportLoading(false);
    }
  };

  // ── Page board apply ──
  const handlePageBoardApply = (updatedProducts) => {
    setProducts((prev) => {
      const emptyRows = prev.filter((p) => !p.product).slice(0, 1);
      const sorted = updatedProducts.sort((a, b) => {
        const pa =
          a.page === "" || a.page === null || a.page === undefined
            ? Infinity
            : Number(a.page);
        const pb =
          b.page === "" || b.page === null || b.page === undefined
            ? Infinity
            : Number(b.page);
        return pa - pb;
      });
      return [...sorted, ...emptyRows];
    });
  };

  // ── Form field helpers ──
  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const openCalendar = useCallback((field) => {
    const ref = field === "valid_from" ? validFromRef : validTillRef;
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setCalendarPosition({ top: rect.bottom + 4, left: rect.left });
    }
    setShowCalendar(field);
  }, []);

  // ── Validation ──
  const validate = () => {
    const errors = {};
    if (!formData.uid.trim()) errors.uid = "UID is required";
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.type) errors.type = "Type is required";
    if (!formData.valid_from) errors.valid_from = "Valid From date is required";
    if (!formData.valid_till) errors.valid_till = "Valid Till date is required";
    if (!clientId && !formData.client_id)
      errors.client_id = "Client is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      uid: formData.uid.trim(),
      name: formData.name.trim(),
      type: formData.type?._id || formData.type,
      client_id: clientId || formData.client_id?._id || formData.client_id,
      valid_from: formData.valid_from || undefined,
      valid_till: formData.valid_till || undefined,
      description: formData.description?.trim() || undefined,
    };

    setLoading(true);
    try {
      let savedCatalogId;
      if (["create", "bulk"].includes(catalogId)) {
        const result = await catalogService.create(payload);
        savedCatalogId = result._id;
      } else {
        await catalogService.edit(payload, catalogId);
        savedCatalogId = catalogId;
      }

      // Sync catalogProduct collection: remove all then recreate
      await catalogProductService.removeAllByCatalog(savedCatalogId);
      const productEntries = products.filter((p) => p.product);
      await catalogProductService.create(
        productEntries.map((p) => ({
          catalog: savedCatalogId,
          product: p.product?._id || p.product,
          page: p.page !== "" ? Number(p.page) : undefined,
          variants: p.variants.map((v) => ({
            sku: v.sku,
            price: v.price !== "" ? Number(v.price) : undefined,
          })),
        })),
      );
      toast.success(
        ["create", "bulk"].includes(catalogId)
          ? "Catalog created successfully!"
          : "Catalog updated successfully!",
      );
      navigate("/catalog?child");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData(initialForm);
    setProducts([{ product: null, page: "", variants: [] }]);
    setFormErrors({});
    setSelectedProducts(new Set());
  };

  // ── Calendar click outside handler ──
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        !event.target.getAttribute("data-calendar-picker")
      ) {
        setShowCalendar(null);
      }
    }
    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendar]);

  const isCreate = catalogId === "create";

  return (
    <Box
      sx={{
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--bg-primary)",
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 2, md: 3 },
          py: 1.5,
          backgroundColor: "var(--bg-paper)",
          // borderBottom: "1px solid var(--border-color)",
          flexShrink: 0,
        }}
      >
        {!isDesktop ? (
          <>
            <IconButton size="small" onClick={() => navigate("/catalog?child")}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography
              fontFamily="Poppins"
              fontWeight={600}
              fontSize="18px"
              color="var(--text-primary)"
            >
              {isCreate ? "Create Catalog" : "Edit Catalog"}
            </Typography>{" "}
          </>
        ) : (
          <></>
        )}
      </Box>

      {/* ── Body ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 1.5, md: 2 },
          py: 2,
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.45)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress color="inherit" sx={{ color: "#fff" }} />
          </Box>
        )}

        {importLoading && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.45)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress color="inherit" sx={{ color: "#fff" }} />
            <Typography color="#fff" fontFamily="Poppins" fontSize={14}>
              Fetching products...
            </Typography>
          </Box>
        )}

        {/* ── Basic Details ── */}
        <SectionPaper title="Catalog Details">
          <Grid container spacing={1}>
            {!clientId && (
              <Grid item xs={12} sm={12} md={12}>
                <FieldLabel required style={{ marginBottom: 0 }}>
                  Client
                </FieldLabel>
                <AutocompleteWithPagination
                  selectedItem={formData.client_id}
                  onDataChange={(val) => setField("client_id", val)}
                  width="100%"
                  optionKey="business_name"
                  backgroundColor="var(--bg-paper)"
                  // customLabel="Search client"
                  hideLabel
                  apiEndPoint={(limit, skip, search) =>
                    clientService.fetch(limit, skip, search, null, "Active")
                  }
                />
              </Grid>
            )}
            {/* UID */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel required>Catalog ID</FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="e.g. CAT-001"
                  value={formData.uid}
                  onChange={(e) => setField("uid", e.target.value)}
                  error={Boolean(formErrors.uid)}
                  helperText={formErrors.uid}
                  sx={fieldSx}
                />
              </Box>
            </Grid>

            {/* Name */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel required>Name</FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Catalog name"
                  value={formData.name}
                  onChange={(e) => setField("name", e.target.value)}
                  error={Boolean(formErrors.name)}
                  helperText={formErrors.name}
                  sx={fieldSx}
                />
              </Box>
            </Grid>

            {/* Type */}
            <Grid item xs={12} sm={6} md={4}>
              <FieldLabel style={{ marginBottom: 0 }} required>
                Type
              </FieldLabel>
              <AutocompleteWithPagination
                selectedItem={formData.type}
                onDataChange={(val) => setField("type", val)}
                width="100%"
                optionKey="name"
                backgroundColor="var(--bg-paper)"
                hideLabel
                apiEndPoint={(limit, skip, search) =>
                  catalogTypeService.fetch(limit, skip * limit, search)
                }
              />
            </Grid>

            {/* Client (admin only) */}

            {/* Valid From */}
            <Grid item xs={12} sm={6} md={6}>
              <Box
                ref={validFromRef}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <FieldLabel>Valid From</FieldLabel>
                <TextField
                  placeholder="Select valid from date"
                  data-calendar-picker="start"
                  size="small"
                  fullWidth
                  value={
                    formData.valid_from
                      ? dayjs(formData.valid_from).format("YYYY-MM-DD")
                      : ""
                  }
                  onFocus={() => openCalendar("valid_from")}
                  inputProps={{ readOnly: true }}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          size="small"
                          onClick={() =>
                            showCalendar === "valid_from"
                              ? setShowCalendar(null)
                              : openCalendar("valid_from")
                          }
                        >
                          <CalendarIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Grid>

            {/* Valid Till */}
            <Grid item xs={12} sm={6} md={6}>
              <Box
                ref={validTillRef}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <FieldLabel>Valid Till</FieldLabel>
                <TextField
                  placeholder="Select valid till date"
                  data-calendar-picker="end"
                  size="small"
                  fullWidth
                  value={
                    formData.valid_till
                      ? dayjs(formData.valid_till).format("YYYY-MM-DD")
                      : ""
                  }
                  onFocus={() => openCalendar("valid_till")}
                  inputProps={{ readOnly: true }}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          size="small"
                          onClick={() =>
                            showCalendar === "valid_till"
                              ? setShowCalendar(null)
                              : openCalendar("valid_till")
                          }
                        >
                          <CalendarIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel>Description</FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Optional catalog description..."
                  value={formData.description}
                  onChange={(e) => setField("description", e.target.value)}
                  sx={fieldSx}
                />
              </Box>
            </Grid>
          </Grid>
        </SectionPaper>

        {/* ── Products Section ── */}
        <SectionPaper
          title={`Products (${products.filter((p) => p.product).length})`}
          action={
            <Box display="flex" alignItems="center" gap={1}>
              {isDesktop ? (
                <>
                  {/* Desktop: text links */}
                  {products.filter((p) => p.product).length > 0 &&
                    formData.type?.name !== "Online" && (
                      <Typography
                        component="span"
                        onClick={() => setPageBoardOpen(true)}
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: 12,
                          color: "primary.main",
                          cursor: "pointer",
                          textDecoration: "underline",
                          "&:hover": { opacity: 0.8 },
                        }}
                      >
                        Page board
                      </Typography>
                    )}
                  <Typography
                    component="span"
                    onClick={() =>
                      !(formData.client_id || clientId)
                        ? null
                        : setImportModalOpen(true)
                    }
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: 12,
                      color: "primary.main",
                      cursor: !(formData.client_id || clientId)
                        ? "not-allowed"
                        : "pointer",
                      textDecoration: "underline",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    Import by filter
                  </Typography>
                  <Tooltip title="Add product">
                    <IconButton
                      disabled={!(formData.client_id || clientId)}
                      size="small"
                      onClick={handleAddProduct}
                      sx={{ color: "primary.main" }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  {/* Non-desktop: icon buttons */}
                  {products.filter((p) => p.product).length > 0 &&
                    formData.type?.name !== "Online" && (
                      <Tooltip title="Page board">
                        <IconButton
                          size="small"
                          onClick={() => setPageBoardOpen(true)}
                          sx={{ color: "primary.main" }}
                        >
                          <ViewColumnOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  <Tooltip title="Import by filter">
                    <IconButton
                      disabled={!(formData.client_id || clientId)}
                      size="small"
                      onClick={() => setImportModalOpen(true)}
                      sx={{ color: "primary.main" }}
                    >
                      <CloudDownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Add product">
                    <IconButton
                      disabled={!(formData.client_id || clientId)}
                      size="small"
                      onClick={handleAddProduct}
                      sx={{ color: "primary.main" }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {selectedProducts.size > 0 && (
                <Tooltip title={`Delete ${selectedProducts.size} selected`}>
                  <IconButton
                    size="small"
                    onClick={handleDeleteSelectedProducts}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          }
        >
          {isDesktop ? (
            <ProductsDesktopTable
              products={products}
              onProductChange={handleProductChange}
              onVariantChange={handleVariantChange}
              onVariantsChange={handleVariantsChange}
              onPageChange={handlePageChange}
              onRemoveProduct={handleRemoveProduct}
              selectedClient={formData.client_id || { _id: clientId }}
              selectedProducts={selectedProducts}
              onToggleProductSelect={handleToggleProductSelect}
              onSelectAllProducts={handleSelectAllProducts}
              hidePageColumn={formData.type?.name === "Online"}
            />
          ) : products.length === 0 ? (
            <Box
              sx={{
                py: 5,
                textAlign: "center",
                border: "2px dashed var(--border-color)",
                borderRadius: 2,
              }}
            >
              <Typography
                fontSize="13px"
                color="var(--text-tertiary)"
                fontFamily="Poppins"
              >
                No products added yet.
              </Typography>
              <Button
                size="small"
                variant="text"
                startIcon={<AddIcon />}
                onClick={handleAddProduct}
                sx={{ mt: 1, textTransform: "none", fontFamily: "Poppins" }}
              >
                Add your first product
              </Button>
            </Box>
          ) : (
            products.map((entry, idx) => (
              <ProductEntry
                key={idx}
                entry={entry}
                entryIndex={idx}
                onProductChange={handleProductChange}
                onVariantChange={handleVariantChange}
                onVariantsChange={handleVariantsChange}
                onPageChange={handlePageChange}
                onRemoveProduct={handleRemoveProduct}
                selectedClient={formData.client_id}
              />
            ))
          )}
        </SectionPaper>

        {/* ── Import Products Modal ── */}
        <ImportProductsModal
          open={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          onImport={handleImportProducts}
          selectedClient={formData.client_id}
          loading={importLoading}
          type={formData.type?.name || ""}
        />
        {formData.type?.name !== "Online" && (
          <PageBoardModal
            open={pageBoardOpen}
            onClose={() => setPageBoardOpen(false)}
            products={products}
            onApply={handlePageBoardApply}
          />
        )}
      </Box>

      {/* ── Footer Actions ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          px: { xs: 2, md: 3 },
          py: 1,
          backgroundColor: "var(--bg-paper)",
          borderTop: "1px solid var(--border-color)",
          flexShrink: 0,
          mb: 3,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleReset}
          disabled={loading}
          sx={{
            textTransform: "none",
            fontFamily: "Poppins",
            color: "var(--text-primary)",
            borderColor: "var(--border-color)",
            backgroundColor: "var(--bg-paper)",
          }}
        >
          Reset
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate("/catalog?child")}
          sx={{
            textTransform: "none",
            fontFamily: "Poppins",
            color: "var(--text-primary)",
            borderColor: "var(--border-color)",
            backgroundColor: "var(--bg-paper)",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ textTransform: "none", fontFamily: "Poppins" }}
        >
          {isCreate ? "Create Catalog" : "Save Changes"}
        </Button>
      </Box>

      {/* Calendar Popover */}
      {showCalendar && calendarPosition && (
        <Box
          ref={calendarRef}
          sx={{
            position: "fixed",
            top: calendarPosition.top,
            left: calendarPosition.left,
            zIndex: 3000,
            bgcolor: "var(--bg-paper)",
            boxShadow: 3,
            borderRadius: 1,
            p: 0.5,
            width: 300,
          }}
        >
          <Calendar
            value={
              showCalendar === "valid_from"
                ? new Date(formData.valid_from || new Date())
                : new Date(formData.valid_till || new Date())
            }
            onChange={(date) => {
              const dateStr = dayjs(date).format("YYYY-MM-DD");
              if (showCalendar === "valid_from") {
                setField("valid_from", dateStr);
              } else if (showCalendar === "valid_till") {
                setField("valid_till", dateStr);
              }
              setShowCalendar(null);
            }}
            className="custom-calendar"
          />
        </Box>
      )}
    </Box>
  );
}
